import { useParams, Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { PublicPage, Seo } from "@/components/public/PublicChrome";
import { LeadCapture } from "@/components/public/LeadCapture";
import { CONTENT_HUBS, HUB_LIST } from "@/pages/learn/contentHubs";
import { TOOLS } from "@/pages/tools/toolsConfig";

// Foundation scaffold for future SEO content hubs. Renders an on-brand hub landing with
// an empty "guides coming soon" state + links to the relevant free tools. No articles yet.
export default function ContentHub() {
  const { hub } = useParams();
  const cfg = CONTENT_HUBS[hub];

  if (!cfg) {
    return (
      <PublicPage>
        <Seo title="Business Guides" path="/learn"
          description="Free business guides and resources from Fairview Systems." />
        <section className="max-w-4xl mx-auto px-6 pt-32 pb-20">
          <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Resources</div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-6" data-testid="learn-index-h1">Business Guides</h1>
          <Link to="/learn/how-to-start-a-business" data-testid="learn-featured-guide"
            className="block rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1C1C1F] to-[#121214] p-6 mb-6 hover:border-[#D4AF37]/60 transition-colors">
            <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">Flagship guide</div>
            <div className="font-heading text-2xl text-white mb-1.5">How to Start a Business — Step-by-Step Checklist</div>
            <p className="text-[#A1A1AA] text-sm">The complete 21-step checklist to go from idea to operating business, with free tools built in.</p>
            <span className="inline-flex items-center gap-1 text-[#D4AF37] text-sm mt-3">Read the free guide <ArrowRight className="w-3.5 h-3.5" /></span>
          </Link>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="learn-index-grid">
            {HUB_LIST.map((h) => (
              <Link key={h.slug} to={`/learn/${h.slug}`} data-testid={`learn-${h.slug}`}
                className="rounded-xl border border-white/10 bg-[#121214] p-5 hover:border-[#D4AF37]/40 transition-colors flex items-center justify-between">
                <span className="text-white">{h.title}</span>
                <ArrowRight className="w-4 h-4 text-[#D4AF37]" />
              </Link>
            ))}
          </div>
        </section>
      </PublicPage>
    );
  }

  return (
    <PublicPage>
      <Seo title={cfg.metaTitle} description={cfg.metaDescription} path={`/learn/${cfg.slug}`} />
      <article className="max-w-4xl mx-auto px-6 pt-32 pb-20">
        <nav className="text-xs text-[#71717A] mb-6">
          <Link to="/" className="hover:text-[#D4AF37]">Home</Link><span className="mx-2">/</span>
          <Link to="/learn" className="hover:text-[#D4AF37]">Guides</Link><span className="mx-2">/</span>
          <span className="text-white">{cfg.title}</span>
        </nav>
        <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Business Guides</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4" data-testid="hub-h1">{cfg.title}</h1>
        <p className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl mb-10">{cfg.intro}</p>

        <div className="rounded-2xl border border-white/10 bg-[#121214] p-8 text-center mb-12" data-testid="hub-empty">
          <BookOpen className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
          <p className="text-[#A1A1AA]">In-depth guides for {cfg.title.toLowerCase()} are coming soon. In the meantime, try our free tools below.</p>
        </div>

        {cfg.relatedTools?.length > 0 && (
          <section className="mb-12">
            <h2 className="font-heading text-2xl text-white mb-4">Free tools to get started</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {cfg.relatedTools.map((slug) => TOOLS[slug] && (
                <Link key={slug} to={`/tools/${slug}`} data-testid={`hub-tool-${slug}`}
                  onClick={() => { import("@/lib/acquisition").then((m) => m.trackAcq("learn_tool_link_click", { tool: slug })); }}
                  className="rounded-xl border border-white/10 bg-[#121214] p-4 hover:border-[#D4AF37]/40 transition-colors flex items-center justify-between">
                  <span className="text-white text-sm">{TOOLS[slug].h1}</span>
                  <ArrowRight className="w-4 h-4 text-[#D4AF37]" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="max-w-2xl"><LeadCapture /></div>
      </article>
    </PublicPage>
  );
}
